import { FormEvent, useEffect, useState } from "react";
import {
  Building2,
  Download,
  ExternalLink,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import type { SessionPayload } from "./AuthenticatedApp";
import { request } from "./FunctionalApp";
async function api(path: string, init: RequestInit = {}) {
  const r = await request(path, init),
    x = await r.json().catch(() => ({}));
  if (!r.ok)
    throw new Error(String(x.error || "Unable to update").replaceAll("_", " "));
  return x;
}
const post = (p: string, b: any) =>
  api(p, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(b),
  });
const when = (date: any) =>
  date ? new Date(date).toLocaleString("en-GB") : "Not scheduled";
const templateNames: Record<string, string> = {
  "carpet-tile": "Carpet-tile factory",
  "food-manufacturing": "Food manufacturing",
  "warehouse-distribution": "Warehouse and distribution",
  "facilities-management": "Facilities management",
  empty: "Empty company",
};
function safe(value: unknown) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
function downloadGuide(sandbox: any) {
  const slug = String(sandbox.slug),
    url = sandbox.url || `https://${slug}.taskroute.co.uk`,
    template =
      templateNames[sandbox.template || sandbox.template_key] || "CMMS sandbox",
    reset = sandbox.nextResetAt || sandbox.next_reset_at,
    resetText = reset
      ? new Date(reset).toLocaleString("en-GB")
      : `Every ${sandbox.resetDays || sandbox.reset_after_days || 30} days`,
    html = `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>TaskRoute ${safe(slug)} sandbox guide</title><style>body{font:16px/1.55 Arial,sans-serif;color:#102a43;max-width:900px;margin:40px auto;padding:0 24px}h1,h2{color:#0b2239}header{border-bottom:4px solid #12a594;margin-bottom:28px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccd6df;padding:10px;text-align:left}th{background:#edf7f5}.warning{background:#fff4dc;border-left:4px solid #e69a00;padding:14px}.card{border:1px solid #d8e0e7;border-radius:8px;padding:18px;margin:18px 0}code{background:#eef3f6;padding:2px 5px}li{margin:7px 0}@media print{body{margin:0}.no-print{display:none}}</style></head><body><header><h1>TaskRoute CMMS sandbox test guide</h1><p><b>Test group:</b> ${safe(slug)} &nbsp; <b>Template:</b> ${safe(template)}</p></header><div class="warning"><b>Fictional testing only.</b> Do not enter real names, production records, photographs, passwords, safety-critical instructions or confidential company information. Keep using your employer's approved maintenance system for real work.</div><div class="card"><h2>Sign in</h2><p><a href="${safe(url)}">${safe(url)}</a></p><table><thead><tr><th>Role</th><th>Email</th><th>Initial password</th></tr></thead><tbody><tr><td>Maintenance team leader</td><td>leader@carpet.demo</td><td>CarpetDemo!2026</td></tr><tr><td>Engineer 1</td><td>engineer1@carpet.demo</td><td>CarpetDemo!2026</td></tr><tr><td>Engineer 2</td><td>engineer2@carpet.demo</td><td>CarpetDemo!2026</td></tr><tr><td>Engineer 3</td><td>engineer3@carpet.demo</td><td>CarpetDemo!2026</td></tr></tbody></table><p><b>Planned clean reset:</b> ${safe(resetText)}. Test data may be erased at that time.</p></div><h2>Engineer checks</h2><ol><li>Open Work orders, claim an unclaimed job for yourself and confirm you cannot assign another engineer.</li><li>Create a fictional mechanical or electrical breakdown with Production Can Run or Production Stopped priority.</li><li>Add notes and activity, complete checklist steps and confirm required steps block early completion.</li><li>Record a temporary fix and permanent-repair due date; confirm the outstanding repair remains visible.</li><li>Open a PPM-derived work order and check its instructions, safety information, tools and planned parts.</li><li>Browse asset history and find inventory by description, manufacturer code or bin.</li><li>Use Helpdesk to follow an escalated issue through claim, production restored and permanent repair.</li><li>Check Notifications after another tester updates or assigns work involving you.</li></ol><h2>Team-leader checks</h2><ol><li>Create and assign a job, add an assistant, review it and sign it off.</li><li>Create or edit a PPM with safety guidance, tools, required checklist steps and planned parts.</li><li>Release the PPM and confirm all details appear on the generated work order.</li><li>Escalate a Helpdesk report before an engineer claims it.</li><li>Review overdue permanent repairs, asset history, inventory movements and Reports.</li></ol><h2>Send feedback</h2><p>Open <b>Feedback</b> in TaskRoute. Include your role, device/browser, page, exact steps, expected result, actual result and impact. Use <b>Blocker</b> for data exposure, data loss, incorrect permissions or anything that prevents further testing.</p><p>Test on a phone or tablet if possible. Report clipped text, difficult buttons, lost form entries, confusing wording and excessive steps as well as functional faults.</p><p class="no-print"><button onclick="window.print()">Print or save as PDF</button></p></body></html>`,
    blob = new Blob([html], { type: "text/html;charset=utf-8" }),
    href = URL.createObjectURL(blob),
    link = document.createElement("a");
  link.href = href;
  link.download = `TaskRoute-${slug}-sandbox-guide.html`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}
export function PlatformAdmin({ session }: { session: SessionPayload }) {
  const [tenants, setTenants] = useState<any[]>([]),
    [feedback, setFeedback] = useState<any[]>([]),
    [slug, setSlug] = useState(""),
    [template, setTemplate] = useState("carpet-tile"),
    [resetDays, setResetDays] = useState(30),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  async function load() {
    try {
      const [tenantRows, feedbackRows] = await Promise.all([
        api("/api/platform/tenants"),
        api("/api/platform/feedback"),
      ]);
      setTenants(tenantRows);
      setFeedback(feedbackRows);
    } catch (e: any) {
      setMessage(e.message);
    }
  }
  useEffect(() => {
    load();
  }, []);
  async function create(e: FormEvent) {
    e.preventDefault();
    if (
      !confirm(
        `Create or fully reset ${slug}.taskroute.co.uk? All existing tester data in that sandbox will be replaced.`,
      )
    )
      return;
    setBusy(true);
    try {
      const x = await post("/api/platform/sandboxes", {
        slug,
        template,
        resetDays,
      });
      setMessage(`${x.slug} is ready and will reset every ${x.resetDays} days`);
      downloadGuide(x);
      setSlug("");
      await load();
    } catch (e: any) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function state(t: any, value: string) {
    if (!confirm(`Change ${t.name} to ${value}?`)) return;
    await post(`/api/platform/tenants/${t.id}/state`, { state: value });
    load();
  }
  async function policy(t: any, patch: any) {
    try {
      await post(`/api/platform/sandboxes/${t.id}/expiry`, {
        resetDays: Number(patch.resetDays ?? t.reset_after_days ?? 30),
        enabled: patch.enabled ?? Boolean(t.reset_enabled),
        allowExport: patch.allowExport ?? Boolean(t.allow_export),
      });
      setMessage(`${t.name} sandbox policy updated`);
      await load();
    } catch (e: any) {
      setMessage(e.message);
    }
  }
  return (
    <div className="platform-admin">
      <header>
        <div>
          <span>◆</span>
          <b>TaskRoute Control</b>
        </div>
        <span>
          <ShieldCheck />
          Staff only
        </span>
        <a href={`https://${session.tenant.slug}.taskroute.co.uk/`}>
          Return to workspace
        </a>
      </header>
      <main>
        <div className="platform-heading">
          <div>
            <h1>Businesses and sandboxes</h1>
            <p>
              Create isolated test businesses and manage their automatic reset,
              export and account policies.
            </p>
          </div>
          <button onClick={load}>
            <RefreshCw />
            Refresh
          </button>
        </div>
        {message ? <div className="func-admin-note">{message}</div> : null}
        <section className="platform-create">
          <h2>Create or reset a test-group sandbox</h2>
          <form onSubmit={create}>
            <label>
              Sandbox template
              <select
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
              >
                <option value="carpet-tile">Carpet-tile factory</option>
                <option value="food-manufacturing">Food manufacturing</option>
                <option value="warehouse-distribution">
                  Warehouse and distribution
                </option>
                <option value="facilities-management">
                  Facilities management
                </option>
                <option value="empty">Empty company</option>
              </select>
            </label>
            <label>
              Sandbox address
              <div>
                <input
                  required
                  pattern="[a-z0-9-]+"
                  placeholder="testgroup1"
                  value={slug}
                  onChange={(e) =>
                    setSlug(
                      e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                    )
                  }
                />
                <span>.taskroute.co.uk</span>
              </div>
            </label>
            <label>
              Automatic clean reset
              <select
                value={resetDays}
                onChange={(e) => setResetDays(Number(e.target.value))}
              >
                <option value={7}>Every 7 days</option>
                <option value={14}>Every 14 days</option>
                <option value={30}>Every 30 days</option>
                <option value={60}>Every 60 days</option>
                <option value={90}>Every 90 days</option>
              </select>
            </label>
            <button disabled={busy}>
              {busy ? "Preparing sandbox…" : "Create / reset sandbox"}
            </button>
          </form>
          <p>
            New sandboxes cannot export data by default. Using an existing name
            deletes and recreates only that sandbox.
          </p>
        </section>
        <section className="platform-tenants">
          <div className="platform-row head">
            <span>Business</span>
            <span>Type / reset</span>
            <span>Users</span>
            <span>Activity</span>
            <span>State</span>
            <span>Actions</span>
          </div>
          {tenants.map((t) => (
            <div className="platform-row" key={t.id}>
              <span>
                <Building2 />
                <b>{t.name}</b>
                <small>{t.slug}.taskroute.co.uk</small>
              </span>
              <span>
                {t.sandbox ? (
                  <>
                    <b>Sandbox</b>
                    <small>
                      {t.reset_enabled
                        ? `Next clean reset: ${when(t.next_reset_at)}`
                        : "Automatic reset paused"}
                    </small>
                    <select
                      value={t.reset_after_days ?? 30}
                      onChange={(e) =>
                        policy(t, { resetDays: Number(e.target.value) })
                      }
                    >
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                      <option value={60}>60 days</option>
                      <option value={90}>90 days</option>
                    </select>
                  </>
                ) : (
                  "Customer"
                )}
              </span>
              <span>{t.users}</span>
              <span>
                {t.work_orders} WOs · {t.ppm_plans} PPMs
              </span>
              <select
                value={t.billing_state}
                onChange={(e) => state(t, e.target.value)}
              >
                <option>ACTIVE</option>
                <option>GRACE</option>
                <option>RESTRICTED</option>
                <option>TERMINATED</option>
              </select>
              <span className="platform-actions">
                {t.sandbox ? (
                  <>
                    <label>
                      <input
                        type="checkbox"
                        checked={Boolean(t.reset_enabled)}
                        onChange={(e) =>
                          policy(t, { enabled: e.target.checked })
                        }
                      />{" "}
                      Auto-reset
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={Boolean(t.allow_export)}
                        onChange={(e) =>
                          policy(t, { allowExport: e.target.checked })
                        }
                      />{" "}
                      Allow export
                    </label>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => downloadGuide(t)}
                    >
                      <Download />
                      Test guide
                    </button>
                  </>
                ) : null}
                <a
                  href={`https://${t.slug}.taskroute.co.uk/`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink />
                  Open
                </a>
              </span>
            </div>
          ))}
        </section>
        <section className="platform-tenants">
          <div className="func-table-head">
            <h2>Tester feedback</h2>
            <span>
              {feedback.filter((x: any) => x.status === "NEW").length} new
            </span>
          </div>
          <div className="platform-row head">
            <span>Tester</span>
            <span>Type</span>
            <span>Feedback</span>
            <span>Page</span>
            <span>Received</span>
            <span>Status</span>
          </div>
          {feedback.map((f: any) => (
            <div className="platform-row" key={f.id}>
              <span>
                <b>{f.user_name}</b>
                <small>
                  {f.tenant_name} · {f.email}
                </small>
              </span>
              <span>{f.category}</span>
              <span>{f.message}</span>
              <span>{f.page || "—"}</span>
              <span>{when(f.created_at)}</span>
              <span>{f.status}</span>
            </div>
          ))}
          {!feedback.length ? (
            <p className="func-empty">
              No tester feedback has been submitted yet.
            </p>
          ) : null}
        </section>
        <p className="platform-footnote">
          A clean reset removes live sandbox records from TaskRoute and
          recreates the fictional factory and accounts. Copies already held in
          encrypted Plesk backups remain until the separate backup retention
          period expires.
        </p>
      </main>
    </div>
  );
}
